import React, { useState } from "react"
import { css } from "emotion"
import EmojiPicker, { EmojiData } from "./EmojiPicker"
import reactToComment from "../util/fetchApiHelpers/reactToComment"
import getUserFromLocalStorage from "../util/getUserFromLocalStorage"
import Tooltip from "./Tooltip"
import generateReactionTooltip from "../util/generateReactionTooltip"
import * as SocketIOClient from "socket.io-client"
import type { Comment, Reaction } from "../types"

let baseReactionStyle = css`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	margin-right: 4px;
	background-color: #f4f5f5;
	padding: 1px 10px;
	margin-top: 0.25em;
	margin-bottom: 0.25em;
	border-radius: 19px;
	border: 1px solid #d6d8d8;
	text-align: center;
	line-height: 23px;
	width: 48px;
	height: 24px;
	cursor: pointer;
	transition: all 0.4s ease-in-out;
	color: #2a3632;
	font-size: 14px;
	font-weight: 500;
	line-height: 20px;
	font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI",
		Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif,
		"Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
`

export interface EmojiReactionsProps {
	JAM_ID: string
	threadId: string
	commentId: string
	reactions: Reaction[]
	onCommentChange: (comment: Comment) => void
	socket: SocketIOClient.Socket
}

export default function EmojiReactions(props: EmojiReactionsProps) {
	let [emojiPickerOpen, setEmojiPickerOpen] = useState(false)
	let [loadingReactions, setLoadingReactions] = useState<Set<Reaction>>(
		new Set(),
	)

	let currentUser = getUserFromLocalStorage()
	let currentUserName = currentUser ? currentUser.name : null

	function toggleEmojiPickerOpen() {
		setEmojiPickerOpen((isOpen) => !isOpen)
	}

	async function handleReaction(emoji: EmojiData) {
		toggleEmojiPickerOpen()

		let comment = await reactToComment(
			props.JAM_ID,
			props.threadId,
			props.commentId,
			(emoji as any).native, // TODO: Support custom emoji?
			"add",
		)

		if (comment) {
			props.onCommentChange(comment)
			props.socket.emit("reactionUpdate", comment)
		}
	}

	async function handleReactionClick(reaction: Reaction) {
		if (!currentUserName) {
			return
		}

		let upvotedUsers = reaction.upvotedUsers
		let action = upvotedUsers.includes(currentUserName) ? "remove" : "add"

		setLoadingReactions((loadingReactions) => {
			let copy = new Set(loadingReactions)
			copy.add(reaction)
			return copy
		})

		let comment = await reactToComment(
			props.JAM_ID,
			props.threadId,
			props.commentId,
			reaction.emoji,
			action,
		)

		if (comment) {
			props.onCommentChange(comment)
			props.socket.emit("reactionUpdate", comment)
		}

		setLoadingReactions((loadingReactions) => {
			let copy = new Set(loadingReactions)
			copy.delete(reaction)
			return copy
		})
	}

	let reactions = props.reactions.filter((reaction) => {
		return reaction.count > 0
	})

	return (
		<div
			className={css`
				display: flex;
				flex-direction: row;
				align-items: center;
				width: fit-content;
				margin-top: 0.75em;
				margin-bottom: 0.75em;
				width: 100%;
				flex-wrap: wrap;
			`}
		>
			{reactions.map((reaction) => {
				let isLoading = loadingReactions.has(reaction)
				let reactionsIncludesCurrentUser = currentUserName
					? reaction.upvotedUsers.includes(currentUserName)
					: false

				let tooltipText = generateReactionTooltip(
					reaction.emoji,
					reaction.upvotedUsers,
				)

				return (
					<Tooltip label={tooltipText} key={reaction.emoji}>
						<div
							onClick={() => handleReactionClick(reaction)}
							className={css`
								${baseReactionStyle}
								${reactionsIncludesCurrentUser &&
								css`
									border: 1px solid #4b81c2;
									background: #eaf3fd;
									color: #4b81c2;
								`}
							`}
						>
							{isLoading ? (
								<span>...</span>
							) : (
								<div
									className={css`
										display: grid;
										grid-gap: 0.3em;
										grid-template-columns: repeat(2, min-content);
									`}
								>
									<span>{reaction.emoji}</span>
									<span>{reaction.count}</span>
								</div>
							)}
						</div>
					</Tooltip>
				)
			})}
			<div
				className={css`
					${baseReactionStyle}

					& > img {
						border-radius: 50px;
						width: 15px;
						height: 15px;
						display: flex;
						align-self: center;
						margin: 0 auto;
						transition: background 0.4s ease-in-out;
					}

					&:hover > img {
						background: #ffbc4c;
					}
				`}
				onClick={toggleEmojiPickerOpen}
			>
				<img src="https://strawberryjam.nyc3.cdn.digitaloceanspaces.com/smile.svg" />
			</div>
			{emojiPickerOpen && <EmojiPicker onSelectEmoji={handleReaction} />}
		</div>
	)
}
